import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { plainToClass } from 'class-transformer';

import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { GlobalValuesService } from 'app/services/global-values.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { FormErrors } from 'app/shared/validators/generic-validators';

import { Organisation } from 'app/shared/models/organisation.model';
import { OrganisationDetails } from 'app/shared/models/org-details.model';
import { User } from 'app/shared/models/user.model';

import { ClientHeaderService } from './../client-header.service';

@Component({
    selector: 'app-organisation-details',
    templateUrl: './organisation-details.component.html',
})

/**
 * Component to update client details.
 */
export class OrganisationDetailsComponent implements OnInit {
    organisationForm: FormGroup;
    orgLegalStructureId: number;
    organisationDetails: OrganisationDetails;
    stockExchangeValues: ApplicationValue[];
    legalStructureValue = '';
    savingClient: boolean;
    isClient: boolean;
    users: User[];

    filteredAccManUsers: Observable<User[]>;
    filteredFinManUsers: Observable<User[]>;

    formErrors = {
        'orgName': '',
        'orgAccManager': ''
    };

    validationMessages = {
        'orgName': {
            'required': 'Required field value.',
        },
        'orgAccManager': {
            'required': 'Required field value.',
        }
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private globalValuesService: GlobalValuesService,
        private warmUpService: WarmUpService,
        private cdr: ChangeDetectorRef,
        private clientHeaderService: ClientHeaderService,
        private snackBarService: SnackBarService) { }

    ngOnInit() {
        this.createForm();
        this.setupControlValues('stockExchangeValues',
            (this.warmUpService.getApplicationValue(ApplicationTypeConstants.STOCK_EXCHANGE)));
        this.setUpUsersList();
        this.updateOrganisationForm();
    }

    setUpUsersList() {
        this.users = this.warmUpService.getUsersList();

        this.filteredAccManUsers = this.organisationForm.get('orgAccManager').valueChanges
            .startWith(null)
            .map(user => user && typeof user === 'object' ? user.name : user)
            .map(name => name ? this.filterUsers(name) : (this.users ? this.users.slice() : this.users));
        this.filteredFinManUsers = this.organisationForm.get('orgFinManager').valueChanges
            .startWith(null)
            .map(user => user && typeof user === 'object' ? user.name : user)
            .map(name => name ? this.filterUsers(name) : (this.users ? this.users.slice() : this.users));
    }

    filterUsers(val: string): User[] {
        return val ? this.users.filter(s => (s.name.toLowerCase().indexOf(val.toLowerCase()) === 0)) : this.users;
    }

    displayFn(user: User): string {
        return user ? user.name : '';
    }

    setupControlValues(control, dropDownValues: any) {
        if (dropDownValues instanceof Observable) {
            dropDownValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this[control] = response.values;
                    this.cdr.detectChanges();
                }
            );
        } else {
            this[control] = dropDownValues;
        }
    }

    createForm() {
        this.organisationForm = this.formBuilder.group({
            orgName: ['', Validators.required],
            orgAccManager: ['', (this.isClient ? Validators.required : [])],
            orgFinManager: [''],
            // orgLegalStructure: ['', Validators.required],
            orgIncorporateDate: [null], // Use null instead of empty string for PrimeNG calendar
            orgStartOfServices: [null],
            orgEndOfServices: [null],
            orgStockExchange: [''],
            orgIsSpecialFI: [''],
            clientIsWTT: [''],
            regCode: [''],
            isin: [''],
        });
        this.organisationForm.valueChanges.subscribe(data => this.onValueChanged(data));
    }

    onValueChanged(data?: any) {
        if (!this.organisationForm) {
            return;
        }
        FormErrors('organisationForm', this);
    }

    updateOrganisationForm(): void {
        if (this.organisationDetails) {
            try {
                console.log('DEBUG: updateOrganisationForm START');
                // Don't mark fields as touched on initial load - only validate them
                // This prevents showing "Required field value" errors for fields that have valid values
                for (let inner in this.organisationForm.controls) {
                    this.organisationForm.get(inner).updateValueAndValidity();
                }
                let accountManager;
                let financeManager;
                let formattedEoS = null;
                let formattedSoS = null;
                let isWTT = true;
                if (this.isClient) {
                    accountManager = this.organisationDetails.clientVO.accountManagerVO;
                    financeManager = this.organisationDetails.clientVO.financeManagerVO;
                    formattedSoS = this.formatDate(this.organisationDetails.clientVO.startOfServices);
                    formattedEoS = this.formatDate(this.organisationDetails.clientVO.endOfServices);
                    isWTT = this.organisationDetails.clientVO.isWTT;
                }
                if (this.organisationDetails.organisationVO.legalStructureId != -1) {
                    this.orgLegalStructureId = this.organisationDetails.organisationVO.legalStructureId;
                    this.legalStructureValue = this.organisationDetails.organisationVO.legalStructureName;
                    // @todo: improve inconsistency between legalStructureValue and legalStructureName.
                }
                
                // Format dates with logging for debugging
                const rawIncorporateDate = this.organisationDetails.organisationVO.incorporatedDate;
                const formattedIncorporateDate = this.formatDate(rawIncorporateDate);
                console.log('DEBUG: Raw incorporatedDate:', rawIncorporateDate, 'Type:', typeof rawIncorporateDate);
                console.log('DEBUG: Formatted incorporatedDate:', formattedIncorporateDate);
                if (this.isClient) {
                    console.log('DEBUG: Raw startOfServices:', this.organisationDetails.clientVO.startOfServices, 'Type:', typeof this.organisationDetails.clientVO.startOfServices);
                    console.log('DEBUG: Formatted startOfServices:', formattedSoS);
                    console.log('DEBUG: Raw endOfServices:', this.organisationDetails.clientVO.endOfServices, 'Type:', typeof this.organisationDetails.clientVO.endOfServices);
                    console.log('DEBUG: Formatted endOfServices:', formattedEoS);
                }
                
                // Build patch object for non-date fields
                const patchData: any = {
                    orgName: this.organisationDetails.organisationVO.name,
                    regCode: this.organisationDetails.organisationVO.registrationCode,
                    orgStockExchange: this.organisationDetails.organisationVO.stockExchangeId,
                    isin: this.organisationDetails.organisationVO.isin,
                    orgIsSpecialFI: this.organisationDetails.organisationVO.isSpecialFinancialInstitution,
                    clientIsWTT: isWTT,
                    orgAccManager: accountManager,
                    orgFinManager: financeManager,
                };
                
                // Use setTimeout to ensure calendar components are fully initialized before setting values
                setTimeout(() => {
                    try {
                        console.log('DEBUG: About to patch non-date form values:', patchData);
                        // First patch non-date fields
                        this.organisationForm.patchValue(patchData, { emitEvent: false });
                        
                        // Then set date fields individually using setValue to avoid triggering validation on all fields
                        // PrimeNG calendar expects Date objects, not strings
                        if (formattedIncorporateDate instanceof Date) {
                            console.log('DEBUG: Setting orgIncorporateDate to Date:', formattedIncorporateDate);
                            try {
                                this.organisationForm.get('orgIncorporateDate').setValue(formattedIncorporateDate, { emitEvent: false });
                            } catch (dateError) {
                                console.error('DEBUG: Error setting orgIncorporateDate:', dateError);
                            }
                        }
                        if (formattedSoS instanceof Date) {
                            console.log('DEBUG: Setting orgStartOfServices to Date:', formattedSoS);
                            try {
                                this.organisationForm.get('orgStartOfServices').setValue(formattedSoS, { emitEvent: false });
                            } catch (dateError) {
                                console.error('DEBUG: Error setting orgStartOfServices:', dateError);
                            }
                        }
                        if (formattedEoS instanceof Date) {
                            console.log('DEBUG: Setting orgEndOfServices to Date:', formattedEoS);
                            try {
                                this.organisationForm.get('orgEndOfServices').setValue(formattedEoS, { emitEvent: false });
                            } catch (dateError) {
                                console.error('DEBUG: Error setting orgEndOfServices:', dateError);
                            }
                        }
                        
                        // Log the actual form values after patching
                        console.log('DEBUG: Form values after patch:', {
                            orgIncorporateDate: this.organisationForm.get('orgIncorporateDate').value,
                            orgStartOfServices: this.organisationForm.get('orgStartOfServices').value,
                            orgEndOfServices: this.organisationForm.get('orgEndOfServices').value
                        });
                        console.log('DEBUG: updateOrganisationForm SUCCESS');
                    } catch (patchError) {
                        console.error('DEBUG: Error during patchValue:', patchError);
                        console.error('DEBUG: Error message:', patchError.message);
                        console.error('DEBUG: Stack trace:', patchError.stack);
                        // Try to continue even if patchValue fails
                    }
                }, 0);
            } catch (error) {
                console.error('DEBUG: Error in updateOrganisationForm:', error);
                console.error('DEBUG: Stack trace:', error.stack);
            }
        }
    }

    /**
     * Converts various date formats to a Date object for PrimeNG calendar.
     * PrimeNG calendar expects Date objects, not strings.
     */
    formatDate(dateField): Date | null {
        console.log('DEBUG formatDate: Input:', dateField, 'Type:', typeof dateField);
        if (!dateField) {
            console.log('DEBUG formatDate: Empty/null value, returning null');
            return null;
        }
        
        // If already a Date object, return it
        if (dateField instanceof Date) {
            if (isNaN(dateField.getTime())) {
                console.log('DEBUG formatDate: Invalid Date object, returning null');
                return null;
            }
            console.log('DEBUG formatDate: Already a Date object, returning as-is');
            return dateField;
        }
        
        // Handle timestamp (number)
        if (typeof dateField === 'number') {
            const date = new Date(dateField);
            if (isNaN(date.getTime())) {
                console.log('DEBUG formatDate: Invalid timestamp, returning null');
                return null;
            }
            console.log('DEBUG formatDate: Converted timestamp to Date:', date);
            return date;
        }
        
        // Handle string formats
        if (typeof dateField === 'string') {
            // Try to parse as date string
            const date = new Date(dateField);
            if (isNaN(date.getTime())) {
                console.log('DEBUG formatDate: Invalid date string, returning null');
                return null;
            }
            console.log('DEBUG formatDate: Parsed string to Date:', date);
            return date;
        }
        
        console.log('DEBUG formatDate: Unknown type, returning null');
        return null;
    }

    /**
     * Normalizes a date value (Date object or string) to ensure it's in yyyy-MM-dd format for backend.
     * Handles Date objects, various string formats (yy-mm-dd, yyyy-MM-dd, etc.) and ensures proper zero-padding.
     */
    normalizeDateString(dateValue: Date | string | null | undefined): string {
        if (!dateValue) {
            return '';
        }
        
        // If it's already a Date object, format it directly
        if (dateValue instanceof Date) {
            if (isNaN(dateValue.getTime())) {
                return '';
            }
            const year = dateValue.getFullYear();
            const month = dateValue.getMonth() + 1;
            const day = dateValue.getDate();
            const monthStr = month < 10 ? '0' + month : '' + month;
            const dayStr = day < 10 ? '0' + day : '' + day;
            return `${year}-${monthStr}-${dayStr}`;
        }
        
        // Handle string formats
        if (typeof dateValue === 'string') {
            // If already in correct format (yyyy-MM-dd), return as is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            // Handle yy-mm-dd format (2-digit year from PrimeNG calendar)
            const twoDigitYearMatch = /^(\d{2})-(\d{2})-(\d{2})$/.test(dateValue);
            if (twoDigitYearMatch) {
                const parts = dateValue.split('-');
                const year = parseInt(parts[0], 10);
                // Convert 2-digit year to 4-digit (assuming 1900-2099 range)
                const fullYear = year < 50 ? 2000 + year : 1900 + year;
                return `${fullYear}-${parts[1]}-${parts[2]}`;
            }
            // Try to parse and reformat other string formats
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) {
                return dateValue; // Return original if parsing fails
            }
            // Format as yyyy-MM-dd
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const monthStr = month < 10 ? '0' + month : '' + month;
            const dayStr = day < 10 ? '0' + day : '' + day;
            return `${year}-${monthStr}-${dayStr}`;
        }
        
        return '';
    }

    onSubmit({ value, valid }) {
        if (this.globalValuesService.gblLegalStructureSelectedIDValue) {
            this.orgLegalStructureId = this.globalValuesService.gblLegalStructureSelectedIDValue;
        }
        var msgSuccess;
        var msgFailure;
        var orgClientId;
        this.savingClient = true;
        const updatedOrganisation = new Organisation();
        updatedOrganisation.name = value.orgName;
        updatedOrganisation.registrationCode = value.regCode;
        // Normalize date to ensure it's in yyyy-MM-dd format expected by backend
        // value.orgIncorporateDate is now a Date object from PrimeNG calendar
        updatedOrganisation.incorporatedDate = this.normalizeDateString(value.orgIncorporateDate);
        updatedOrganisation.isin = value.isin;
        updatedOrganisation.legalStructureId = this.orgLegalStructureId;
        updatedOrganisation.stockExchangeId = value.orgStockExchange == -1 ? null : value.orgStockExchange;
        updatedOrganisation.isSpecialFinancialInstitution = value.orgIsSpecialFI ? value.orgIsSpecialFI : false;
        if (this.isClient) {
            updatedOrganisation.accountManagerId = value.orgAccManager ? value.orgAccManager.userId : null;
            updatedOrganisation.accountManagerName = value.orgAccManager ? value.orgAccManager.name : null;
            updatedOrganisation.financeManagerId = value.orgFinManager ? value.orgFinManager.userId : null;
            updatedOrganisation.financeManagerName = value.orgFinManager ? value.orgFinManager.name : null;
            // Normalize dates to ensure they're in yyyy-MM-dd format expected by backend
            // value.orgStartOfServices and value.orgEndOfServices are now Date objects from PrimeNG calendar
            updatedOrganisation.endOfService = this.normalizeDateString(value.orgEndOfServices);
            updatedOrganisation.startOfService = this.normalizeDateString(value.orgStartOfServices);
            updatedOrganisation.isWTT = value.clientIsWTT;

            msgSuccess = MessageService.UPDATE_CLIENT_SUCCESS;
            msgFailure = MessageService.UPDATE_CLIENT_FAILURE;
            orgClientId = this.organisationDetails.clientVO.id;
        } else {
            msgSuccess = MessageService.UPDATE_ORGANISATION_SUCCESS;
            msgFailure = MessageService.UPDATE_ORGANISATION_FAILURE;
            orgClientId = this.organisationDetails.organisationVO.id;
        }

        this.clientHeaderService
            .saveOrgClientDetails(updatedOrganisation, orgClientId, this.isClient)
            .subscribe(response => {
                this.savingClient = false;
                if (response.flagResult) {
                    this.dialogRef.close(response.arrResult);
                }
                this.callSnackBarService(response, msgSuccess, msgFailure);
            },
            err => {
                this.savingClient = false;
                this.callSnackBarService(err, msgSuccess, msgFailure);
            });
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }
}




// WEBPACK FOOTER //
// ./src/app/clients/client/header/organisationdetails/organisation-details.component.ts